import { isEmpty } from 'lodash';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Dotdotdot } from '@hivebrite/design-system/Dotdotdot';
import { type EmbedType } from '@hivebrite/design-system/Embed';
import { Link } from '@hivebrite/design-system/Link';
import { useEmbedLy } from '@hivebrite/react-hooks';

import { MEDIA_CENTER_COVER_SIZE } from 'hb-react/backoffice/apps/MediaCenter/constants';
import Embed from 'hb-react/shared/components/DesignSystem/Embed';
import onEnterOrSpacePress from 'hb-react/shared/utils/onEnterOrSpacePress';

import WrapperContent from '../WrapperContent';

interface ExternalSourceThumbnailProps {
  children?: React.ReactNode;
  openPlayVideo?: () => void;
  text?: React.ReactNode;
  thumbnailUrl?: string;
  to: {
    [key: string]: any;
    pathname: string;
  };
  url: string;
}

const ExternalSourceThumbnail = ({
  children,
  openPlayVideo,
  text,
  url,
  to,
  thumbnailUrl = undefined,
}: ExternalSourceThumbnailProps) => {
  const { response, isLoading } = useEmbedLy(url, {
    apiUrl: `${window.location.origin}${Routes.embed_path()}`,
  });

  const embed = response as EmbedType;

  const stringyfieldText = text ? text.toString() : '';

  const isRichOrLinkType = embed?.type === 'rich' || embed?.type === 'link';

  if (!isEmpty(embed) && !(isRichOrLinkType && thumbnailUrl)) {
    return (
      <ExternalSourceWrapper
        data-testid="external-embedly-wrapper"
        embed={embed}
        openPlayVideo={openPlayVideo}
      >
        <WrapperContent
          tabIndex={0}
          aria-label={s__(
            'Accessibility|Click to see the preview of %{linkName}',
          ).i__({
            linkName: stringyfieldText,
          })}
          css={css`
            & > div {
              height: 100%;
            }
            & > div > div {
              height: 100%;
            }
          `}
        >
          {thumbnailUrl ? (
            <WrapperContent
              justifyContent="center"
              data-testid="thumbnail-link-to-preview"
            >
              <StyledImg alt={stringyfieldText} src={thumbnailUrl} />
            </WrapperContent>
          ) : (
            <Embed
              embed={
                {
                  ...embed,
                  url,
                } as EmbedType
              }
              linkUrl={to.pathname}
              isInternalLink
              isLoading={isLoading}
              roundCorners="none"
              withDescription={false}
              onVideoPlay={openPlayVideo}
              mediaAspectRatio={MEDIA_CENTER_COVER_SIZE.aspectRatio}
              renderEmptyPreview={(embed) => (
                <Box
                  height="100%"
                  width="100%"
                  alignItems="center"
                  justifyContent="center"
                  font="h2-regular"
                  bg="white"
                >
                  <Dotdotdot clamp={2}>{embed.provider_name}</Dotdotdot>
                </Box>
              )}
            />
          )}
        </WrapperContent>
      </ExternalSourceWrapper>
    );
  }

  return (
    <>
      {thumbnailUrl ? (
        <Link to={to}>
          <WrapperContent
            justifyContent="center"
            data-testid="thumbnail-link-to-preview"
          >
            <StyledImg alt={stringyfieldText} src={thumbnailUrl} />
          </WrapperContent>
        </Link>
      ) : (
        <WrapperContent tabIndex={0} data-testid="external-wrapper">
          <Box
            width="100%"
            alignItems="center"
            justifyContent="center"
            font="h2-regular"
            textAlign="center"
            p={6}
          >
            <Dotdotdot clamp={2}>{children || text}</Dotdotdot>
          </Box>
        </WrapperContent>
      )}
    </>
  );
};

export default ExternalSourceThumbnail;

interface ExternalSourceWrapperProps {
  children: React.ReactNode;
  embed: EmbedType;
  openPlayVideo?: () => void;
}

export const ExternalSourceWrapper = ({
  children,
  embed,
  openPlayVideo,
}: ExternalSourceWrapperProps) => {
  const isEmbedVideoOnHtml = !!embed && embed.type === 'video' && !!embed.html;

  if (!isEmbedVideoOnHtml) {
    return children;
  }

  return (
    <Box
      display="block"
      style={{ cursor: 'pointer' }}
      onClick={openPlayVideo}
      onKeyDown={onEnterOrSpacePress(openPlayVideo)}
      aria-label={__('Start the video preview')}
      data-testid="embedly-wrapper"
    >
      {children}
    </Box>
  );
};

const StyledImg = styled.img.attrs(() => ({
  height: 'auto',
  width: '100%',
}))`
  object-fit: cover;
`;
